import { TREATMENT_CASE_URL } from "./url"
import { getViewerUniqueId } from "../lib/viewer-id"
import clientApi from "@/shared/api/client"
import { AxiosError } from "axios"
import { ApiResponse } from "@/shared/types/interface/api"
import {
  TREATMENT_CASE_SORT,
  type TreatmentCaseSort,
  TreatmentCaseListResponse,
  TreatmentCaseSummaryResponse,
} from "@/shared/types/type/treatment"
import { ERROR_CODE } from "@/shared/constants/errorCode"

export const getHospitalTreatmentCaseListClient = async (hospitalOriginId: number, page: number, sort: string, treatmentSubjectId?: number) => {
  try {
    const response = await clientApi.get(TREATMENT_CASE_URL.HOSPITAL_TREATMENT_CASE_LIST(hospitalOriginId, page, sort, treatmentSubjectId))
    if (response.status !== 200) {
      throw new Error("Failed to get hospital treatment case list")
    }
    if (response.data.status !== 200) {
      const error = new AxiosError(response.data.message || "Failed to get hospital treatment case list")
      error.response = {
        ...response,
        status: response.data.status,
        statusText: response.data.message || "Error",
      } as AxiosError["response"]
      throw error
    }
    return response.data as ApiResponse<TreatmentCaseListResponse>
  } catch (error) {
    if (error instanceof AxiosError) {
      if (!error.response) {
        throw new Error("No Response")
      }
    }
    throw error
  }
}

export const getTreatmentCaseSummaryListClient = async (categoryId?: number) => {
  try {
    const response = await clientApi.get(TREATMENT_CASE_URL.SUMMARY_LIST(categoryId ?? null))
    if (response.status !== 200) {
      throw new Error("Failed to get treatment case summary list")
    }
    return response.data as ApiResponse<TreatmentCaseSummaryResponse[]>
  } catch (error) {
    if (error instanceof AxiosError) {
      if (!error.response) {
        throw new Error("No Response")
      }
    }
    throw error
  }
}

export const getTreatmentCaseListClient = async (categoryId?: number, subjectId?: number, sort?: TreatmentCaseSort, page?: number) => {
  try {
    const response = await clientApi.get(
      TREATMENT_CASE_URL.TREATMENT_CASE_LIST(categoryId ?? null, subjectId ?? null, sort ?? TREATMENT_CASE_SORT.VIEW_COUNT_DESC, page ?? 1, 10)
    )
    if (response.status !== 200) {
      throw new Error("Failed to get treatment case list")
    }
    return response.data as ApiResponse<TreatmentCaseListResponse>
  } catch (error) {
    if (error instanceof AxiosError) {
      if (!error.response) {
        throw new Error("No Response")
      }
    }
    throw error
  }
}

export const postTreatmentCaseHelpfulClient = async (treatmentCaseId: number) => {
  try {
    const response = await clientApi.post(TREATMENT_CASE_URL.HELPFUL(treatmentCaseId))
    if (response.status !== 200) {
      throw new Error("Failed to post treatment case helpful")
    }
    if (response.data.status !== 200) {
      const error = new AxiosError(ERROR_CODE[response.data.code as keyof typeof ERROR_CODE] || "Unknown Error")
      error.response = {
        ...response,
        status: response.data.status,
        statusText: response.data.message || "Error",
      } as AxiosError["response"]
      throw error
    }
    return response.data
  } catch (error) {
    if (error instanceof AxiosError) {
      if (!error.response) {
        throw new Error("No Response")
      }
    }
    throw error
  }
}

export const deleteTreatmentCaseHelpfulClient = async (treatmentCaseId: number) => {
  try {
    const response = await clientApi.delete(TREATMENT_CASE_URL.HELPFUL(treatmentCaseId))
    if (response.status !== 200) {
      throw new Error("Failed to delete treatment case helpful")
    }
    if (response.data.status !== 200) {
      const error = new AxiosError(ERROR_CODE[response.data.code as keyof typeof ERROR_CODE] || "Unknown Error")
      error.response = {
        ...response,
        status: response.data.status,
        statusText: response.data.message || "Error",
      } as AxiosError["response"]
      throw error
    }
    return response.data
  } catch (error) {
    if (error instanceof AxiosError) {
      if (!error.response) {
        throw new Error("No Response")
      }
    }
    throw error
  }
}

/**
 * 조회 등록 — 릴리가 만든 중복 감지 경로(POST /view, uniqueId 유니크 인덱스 → 큐 → +1)를 태운다.
 * 실패는 호출부에서 무시한다: 조회수는 화면 기능과 무관하고, 봇·JS 미실행 접근은
 * 이 코드까지 오지 못하므로 집계에서 자연히 빠진다.
 */
export const registerTreatmentCaseViewClient = async (treatmentCaseId: number) => {
  const uniqueId = getViewerUniqueId()
  if (!uniqueId) return
  await clientApi.post(TREATMENT_CASE_URL.VIEW(treatmentCaseId), { uniqueId })
}
